import { useCallback, useState, type ReactNode } from 'react';
import { directReports, type Directory, type User } from '@gdec/shared';
import { api, errorMessage } from '@/api/client';
import { Modal } from '@/components/Modal';
import { Spinner } from '@/components/Spinner';
import { useToast } from '@/components/Toast';

export interface DeactivateModalProps {
  target: User;
  directory: Directory;
  onClose: () => void;
  onChanged: () => Promise<void>;
}

export function DeactivateModal({
  target,
  directory,
  onClose,
  onChanged,
}: DeactivateModalProps): ReactNode {
  const { toast } = useToast();
  const [reason, setReason] = useState('');
  const [separationDate, setSeparationDate] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reports = directReports(directory, target.id);

  const submit = useCallback(async (): Promise<void> => {
    if (!reason.trim()) {
      setError('A reason is required — it is recorded in the audit trail.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.admin.deactivateUser<User>({
        userId: target.id,
        reason: reason.trim(),
        // The server rejects an empty date rather than treating it as absent.
        ...(separationDate ? { separationDate } : {}),
      });
      await onChanged();
      toast(`${target.name} deactivated.`, 'ok');
      onClose();
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }, [onChanged, onClose, reason, separationDate, target.id, target.name, toast]);

  return (
    <Modal title="Deactivate Employee" onClose={busy ? () => undefined : onClose}>
      <p style={{ fontSize: 12.5 }}>
        Deactivating <b>{target.name}</b> removes them from the active organization chart and from
        approval routing, blocks their sign-in and any new claims, and frees the manager slot they
        occupy. Their historical claims, approvals and audit history stay intact under their name.
      </p>
      <p className="panel-note">
        The server refuses this while they still own claims awaiting their action — reassign or
        complete those first.
      </p>
      <div className="form-row">
        <div className="field">
          <label htmlFor="deact-date">Separation Date</label>
          <input
            id="deact-date"
            type="date"
            value={separationDate}
            onChange={(e) => setSeparationDate(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="deact-reason">Reason</label>
          <input
            id="deact-reason"
            value={reason}
            placeholder="e.g. Resigned"
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
      </div>
      {reports.length > 0 ? (
        <p style={{ fontSize: 11.5, color: 'var(--warn)' }}>
          {reports.length} employee(s) currently report to {target.name}. Approval routing will
          continue to the next active manager above them.
        </p>
      ) : null}
      {error ? <div className="error-text">{error}</div> : null}
      <div className="cf-submitbar">
        <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-danger"
          onClick={() => void submit()}
          disabled={busy}
        >
          {busy ? <Spinner /> : null}
          Deactivate {target.name}
        </button>
      </div>
    </Modal>
  );
}
